/**
 * Shows VND prices in USD (display only; checkout is charged in VND).
 * Loaded from layout/theme.liquid on the English site when the "USD prices" theme setting is on.
 * Finds VND amounts in text ("850,000₫", "850.000 ₫", "850,000₫ VND", "₫850,000") and rewrites them,
 * keeping the original in a WeakMap so updated prices (variant change, cart refresh, more products)
 * are converted again by a MutationObserver.
 */
(() => {
  const config = window.inebasCurrency;
  if (!config || !config.rate) return;

  const rate = Number(config.rate);
  const cents = config.rounding === 'cents';
  const VND = /(₫\s?)?(\d{1,3}(?:[.,]\d{3})+|\d+)(\s?₫)?(\s?VND)?/g;
  const SKIP = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'INPUT', 'SELECT', 'OPTION', 'NOSCRIPT']);
  const originals = new WeakMap();

  const format = (vnd) => {
    const usd = vnd / rate;
    return cents
      ? '$' + usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '$' + Math.round(usd).toLocaleString('en-US');
  };

  const convertText = (text) =>
    text.replace(VND, (match, before, number, after, code) => {
      if (!before && !after && !code) return match; // a plain number, not a price
      const vnd = Number(number.replace(/[.,\s]/g, ''));
      return Number.isFinite(vnd) ? format(vnd) : match;
    });

  const convertNode = (node) => {
    const source = originals.has(node) && node.nodeValue === originals.get(node).converted
      ? originals.get(node).original
      : node.nodeValue;
    if (!source || !/₫|VND/.test(source)) return;
    const converted = convertText(source);
    if (converted === source || converted === node.nodeValue) return;
    originals.set(node, { original: source, converted });
    node.nodeValue = converted;
  };

  const walk = (root) => {
    if (root.nodeType === Node.TEXT_NODE) {
      if (!SKIP.has(root.parentNode?.nodeName)) convertNode(root);
      return;
    }
    if (root.nodeType !== Node.ELEMENT_NODE || SKIP.has(root.nodeName)) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) => (SKIP.has(n.parentNode?.nodeName) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
    });
    let n;
    while ((n = walker.nextNode())) convertNode(n);
  };

  const start = () => {
    walk(document.body);
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'characterData') convertNode(m.target);
        for (const added of m.addedNodes) walk(added);
      }
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
    document.documentElement.classList.add('currency-usd');
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
